import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, CheckCircle2, AlertTriangle, ShieldCheck, FileText } from 'lucide-react';
import { SignaturePad, MethodTabs } from './SignPlanOfCarePage';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const post = (path, body) => fetch(`${API_URL}/api/consent-sign/${path}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
const Shell = ({ children, testId }) => (
  <div className="min-h-screen bg-gray-950 px-4 py-6 sm:py-10" data-testid={testId}>
    <div className="max-w-2xl mx-auto">{children}</div>
  </div>
);
const Card = ({ children }) => <div className="bg-white rounded-2xl p-5 sm:p-6 shadow-xl">{children}</div>;

const DobGate = ({ info, onPass }) => {
  const [dob, setDob] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true); setErr('');
    const r = await post(`${info.token}/verify`, { dob });
    const d = await r.json().catch(() => ({}));
    setBusy(false);
    if (r.ok) onPass(dob, d.patientName); else setErr(d.detail || 'Could not verify');
  };
  return (
    <Card>
      <ShieldCheck className="w-10 h-10 text-[#b8941f] mb-3" />
      <h2 className="text-xl font-semibold text-gray-900">Hello {info.firstName}</h2>
      <p className="text-sm text-gray-600 mt-1 mb-4">To protect your privacy, please enter your date of birth.</p>
      <input type="date" value={dob} onChange={e => setDob(e.target.value)} className="w-full px-3 py-3 border rounded-lg text-base" data-testid="consent-dob-input" />
      {err && <p className="text-sm text-red-600 mt-2" data-testid="consent-dob-error">{err}</p>}
      <button onClick={go} disabled={!dob || busy} className="mt-4 w-full py-3 rounded-full bg-[#d4af37] text-gray-900 font-semibold disabled:opacity-40" data-testid="consent-dob-submit">
        {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Continue'}
      </button>
    </Card>
  );
};

const SignerPick = ({ signer, setSigner }) => (
  <div className="grid grid-cols-2 gap-2 mb-4" data-testid="consent-signer">
    {[['patient', 'I am the patient'], ['representative', 'I sign for the patient']].map(([id, label]) => (
      <button key={id} type="button" onClick={() => setSigner(id)} data-testid={`consent-signer-${id}`}
        className={`py-2.5 rounded-lg border text-sm font-medium transition-colors ${signer === id ? 'bg-gray-900 text-[#d4af37] border-gray-900' : 'text-gray-700 hover:bg-gray-50'}`}>{label}</button>
    ))}
  </div>
);

const SignForm = ({ token, dob, patientName, onDone }) => {
  const [pdfUrl, setPdfUrl] = useState('');
  const [signer, setSigner] = useState('patient');
  const [method, setMethod] = useState('drawn');
  const [image, setImage] = useState(null);
  const [typedName, setTypedName] = useState(patientName || '');
  const [rep, setRep] = useState({ name: '', relationship: '' });
  const [attest, setAttest] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [pages, setPages] = useState([]);
  useEffect(() => {
    post(`${token}/pdf`, { dob }).then(r => (r.ok ? r.blob() : null)).then(b => b && setPdfUrl(URL.createObjectURL(b)));
    post(`${token}/pages`, { dob }).then(r => (r.ok ? r.json() : null)).then(d => d && setPages(d.pages || []));
  }, [token, dob]);
  const isRep = signer === 'representative';
  const ready = attest && (method === 'drawn' ? !!image : typedName.trim().length >= 3) && (!isRep || (rep.name.trim().length >= 3 && rep.relationship.trim()));
  const submit = async () => {
    setBusy(true); setErr('');
    const r = await post(`${token}/sign`, { dob, signer, method, typedName: method === 'typed' ? typedName : (isRep ? rep.name : patientName), signatureImage: image,
      representativeName: rep.name, relationship: rep.relationship, attest });
    const d = await r.json().catch(() => ({}));
    setBusy(false);
    if (r.ok) onDone(d.message); else setErr(d.detail || 'Signing failed');
  };
  return (
    <div className="space-y-4">
      <Card>
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-gray-700"><FileText className="w-4 h-4 inline mr-1" /> Admission Consent for <strong>{patientName}</strong></p>
          {pdfUrl && <a href={pdfUrl} target="_blank" rel="noreferrer" className="text-sm text-[#b8941f] underline whitespace-nowrap" data-testid="consent-open-pdf">Open full form</a>}
        </div>
        <div className="mt-3 max-h-[60vh] overflow-y-auto rounded-lg border bg-gray-50 space-y-2 p-1" data-testid="consent-pdf-preview">
          {pages.length ? pages.map((p, i) => <img key={i} src={`data:image/png;base64,${p}`} alt={`Consent page ${i + 1}`} className="w-full bg-white shadow-sm" data-testid={`consent-page-${i + 1}`} />)
            : <div className="h-64 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}
        </div>
      </Card>
      <Card>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Sign</h2>
        <SignerPick signer={signer} setSigner={setSigner} />
        {isRep && (
          <div className="grid sm:grid-cols-2 gap-2 mb-4">
            <input value={rep.name} onChange={e => setRep({ ...rep, name: e.target.value })} placeholder="Your full name" className="px-3 py-2.5 border rounded-lg" data-testid="consent-rep-name" />
            <input value={rep.relationship} onChange={e => setRep({ ...rep, relationship: e.target.value })} placeholder="Relationship (e.g. daughter)" className="px-3 py-2.5 border rounded-lg" data-testid="consent-rep-relationship" />
          </div>
        )}
        <MethodTabs method={method} setMethod={setMethod} />
        {method === 'drawn' ? <SignaturePad onChange={setImage} /> : (
          <div>
            <input value={typedName} onChange={e => setTypedName(e.target.value)} className="w-full px-3 py-2.5 border rounded-lg" placeholder="Full name" data-testid="consent-typed-name" />
            {typedName && <p className="mt-3 text-3xl text-[#0b1f5c]" style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>{typedName}</p>}
          </div>
        )}
        <label className="flex items-start gap-2 mt-4 text-sm text-gray-700">
          <input type="checkbox" checked={attest} onChange={e => setAttest(e.target.checked)} className="mt-1 w-4 h-4" data-testid="consent-attest" />
          I have read the Admission Consent, I agree to it, and I am applying my electronic signature.
        </label>
        {err && <p className="mt-3 text-sm text-red-600" data-testid="consent-sign-error">{err}</p>}
        <button onClick={submit} disabled={!ready || busy} className="mt-4 w-full py-3 rounded-full bg-[#d4af37] text-gray-900 font-semibold disabled:opacity-40 hover:bg-[#c9a227] transition-colors flex items-center justify-center gap-2" data-testid="consent-sign-submit">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Sign and Send
        </button>
      </Card>
    </div>
  );
};

export default function SignConsentPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [pass, setPass] = useState(null);
  const [done, setDone] = useState('');
  useEffect(() => {
    fetch(`${API_URL}/api/consent-sign/${token}`).then(async r => {
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setError(d.detail || 'This link is not valid'); return; }
      setInfo({ ...d, token });
      if (!d.requireDob) {
        const v = await post(`${token}/verify`, {});
        const vd = await v.json().catch(() => ({}));
        setPass({ dob: '', name: vd.patientName || d.firstName });
      }
    }).catch(() => setError('Could not reach the server'));
  }, [token]);

  if (done) return (
    <Shell testId="consent-sign-success"><Card><div className="text-center py-6">
      <CheckCircle2 className="w-14 h-14 text-green-600 mx-auto mb-3" />
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Signed</h1><p className="text-gray-600">{done}</p>
    </div></Card></Shell>
  );
  return (
    <Shell testId="consent-sign-page">
      <header className="mb-5">
        <p className="text-[#d4af37] text-xs tracking-widest uppercase">{info?.agency || 'IVORY Home Health Care'}</p>
        <h1 className="text-3xl font-bold text-white mt-1">Admission Consent</h1>
      </header>
      {error && <Card><p className="flex items-center gap-2 text-red-700" data-testid="consent-link-error"><AlertTriangle className="w-5 h-5" /> {error}</p></Card>}
      {info && !pass && info.requireDob && <DobGate info={info} onPass={(dob, name) => setPass({ dob, name })} />}
      {info && pass && <SignForm token={token} dob={pass.dob} patientName={pass.name} onDone={setDone} />}
    </Shell>
  );
}
